/**
 * ArtByScene.jsx — Phase 3 read-only + Phase 4 editing "By Scene" view.
 *
 * Table view = blocks per scene; Cards view = 3-column grid.
 * Drawer opens on row/card click. Lightbox opens on any inline thumbnail.
 *
 * Phase 4 (writes enabled, 2026-02):
 *   • Drawer footer carries explicit Save / Cancel buttons (NOT autosave).
 *   • Prop chips: add / edit-description / delete / attach photo.
 *   • Look & feel: add manual palette, upload/AI-generate mood images,
 *     scoped via additive `scope="scene"` + `scope_id=scene.id`.
 *   • Scene art notes: additive `scenes.art_notes` field via
 *     `PATCH /scenes/{id}` (SceneUpdate now includes `art_notes`).
 *   • All uploads go through the backend `register_upload_file()` helper.
 *   • Write affordances hidden when `useProjectRole(pid).permissions.canWrite`
 *     is false (director/DoP/producer read-only view remains untouched).
 *
 * Category mapping — legacy UPPERCASE `breakdown_items.category` enum
 * (24k live docs; per PRD S2 kept untouched) → PRD UI columns:
 *   • `PICTURE_VEHICLE`     → column: "Picture Vehicle"
 *   • `ART_SET_DRESSING`    → column: "Set Dressing"
 *   • `PROP` w/ `asset_sub_type` ∈ {weapon, hero_prop, action_prop}
 *                           → column: "Action Props"
 *   • all other `PROP`      → column: "Scene Props"     (default bucket)
 *
 * All photos render via <AuthImage> + dtoken (SEC-002.6). No <img src>.
 * All labels use `label()` from `/utils/artI18n.js` so Phase 11 can localize.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Search, Filter, Download, Plus, MapPin,
         Trash2, Sparkles, Upload, Check } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { formatSceneNumber } from '../../utils/sceneNumber';
import { label as L } from '../../utils/artI18n';
import { useProjectRole } from '../../hooks/useProjectRole';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  ART_COL_TOKENS, PANEL_DIVIDER, TypeBadge, TodBadge,
  PropCol, Lightbox, BulkAddModal,
  ReqPriorityPill, ReqCategoryPill, ReqStatusPill,
} from './artShared';
import { useAssetActions } from './AssetActionsContext';
import { useRequirementActions } from './RequirementActionsContext';
import AiSuggestPanel from './AiSuggestPanel';
import BreakdownItemEditModal from './BreakdownItemEditModal';
import NotesPhotoStrip from './NotesPhotoStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_PICTURE_VEHICLE = 'PICTURE_VEHICLE';
const CAT_SET_DRESSING    = 'ART_SET_DRESSING';
const CAT_PROP            = 'PROP';
// Phase 4 (2026-02) — new category emitted by the Phase 2 parser.
const CAT_SCENE_PROP      = 'SCENE_PROP';

function bucketOf(item) {
  const c = (item.category || '').toUpperCase();
  if (c === CAT_PICTURE_VEHICLE) return 'picture_vehicle';
  if (c === CAT_SET_DRESSING)    return 'set_dressing';
  // Phase 4 (2026-02) — nullable is_action_prop override.
  //   is_action_prop === true  → user explicitly promoted to Action Props
  //   is_action_prop === false → user explicitly demoted to Scene Props
  //   is_action_prop === null/undefined → trust parser: PROP = action,
  //                                       SCENE_PROP = scene
  // Case matrix:
  //   Legacy PROP (backfilled False) → scene_props  (Decision 1(b))
  //   Legacy PROP promoted (True)    → action_props
  //   New parser PROP (null)         → action_props (post-Phase-2)
  //   New parser SCENE_PROP (null)   → scene_props
  //   User-promoted SCENE_PROP       → action_props
  //   User-demoted new PROP          → scene_props
  if (item.is_action_prop === true)  return 'action_props';
  if (item.is_action_prop === false) return 'scene_props';
  if (c === CAT_PROP)       return 'action_props';
  if (c === CAT_SCENE_PROP) return 'scene_props';
  return null; // outside the 4 art columns — hidden from By Scene
}

// TypeBadge, TodBadge, Chip, PropCol, Lightbox all moved to
// `./artShared.jsx` (Phase 5, 2026-02) so ArtByLocation can reuse them.

function SceneBlock({ scene, buckets, onOpen, onPhotoClick, canWrite, onEditFull }) {
  const hasAddNotes = !!(scene.additional_notes && scene.additional_notes.trim());
  const hasArtNotes = !!(scene.art_notes && scene.art_notes.trim());
  return (
    <div
      className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-panel)] hover:border-[var(--color-accent)] cursor-pointer transition"
      onClick={() => onOpen(scene)}
      data-testid={`art-scene-block-${scene.scene_number}`}
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
        <span className="text-sm font-bold text-[var(--color-accent)]" data-testid="art-scene-num">#{formatSceneNumber(scene.scene_number)}</span>
        <MapPin className="w-3 h-3 text-[var(--color-text-dim)]" />
        <span className="text-sm font-bold text-[var(--color-text)] truncate">{scene.location_name || '—'}</span>
        <TypeBadge type={scene.location_type} />
        <TodBadge tod={scene.time_of_day} />
        <span className="ml-auto text-[10px] text-[var(--color-accent)]">{L('common.more_details')} ▸</span>
      </div>
      {scene.synopsis && (
        <div className="px-3 py-2 text-[11px] text-[var(--color-text)] bg-[var(--color-panel-alt)] border-b border-[var(--color-border)] leading-relaxed line-clamp-2">
          {scene.synopsis_en || scene.synopsis}
        </div>
      )}
      {/* Phase 4.5: 3 tinted panels with 1px vertical dividers between them */}
      <div
        className="grid grid-cols-3 gap-0 px-3 py-2 border-b border-[var(--color-border)]"
        style={{ columnGap: '1px', background: PANEL_DIVIDER }}
      >
        <PropCol title={ART_COL_TOKENS.action_props.label} colKey="action_props" items={buckets.action_props} onPhotoClick={onPhotoClick} canWrite={canWrite} onEditFull={onEditFull} alwaysAllowEditFull />
        <PropCol title={ART_COL_TOKENS.scene_props.label}  colKey="scene_props"  items={buckets.scene_props}  onPhotoClick={onPhotoClick} canWrite={canWrite} onEditFull={onEditFull} alwaysAllowEditFull />
        <PropCol title={ART_COL_TOKENS.set_dressing.label} colKey="set_dressing" items={buckets.set_dressing} onPhotoClick={onPhotoClick} canWrite={canWrite} onEditFull={onEditFull} alwaysAllowEditFull />
      </div>
      {buckets.picture_vehicle.length > 0 && (
        <div className="px-3 py-2 border-b border-[var(--color-border)]">
          <PropCol title={ART_COL_TOKENS.picture_vehicle.label} colKey="picture_vehicle" items={buckets.picture_vehicle} onPhotoClick={onPhotoClick} canWrite={canWrite} onEditFull={onEditFull} alwaysAllowEditFull />
        </div>
      )}
      {hasArtNotes && (
        <div className="px-3 py-2 text-[11px] text-[var(--color-text-sec)] border-b border-[var(--color-border)] leading-relaxed line-clamp-2"
             data-testid={`art-scene-block-${scene.scene_number}-notes`}>
          <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mr-1">Notes:</span>
          {scene.art_notes}
        </div>
      )}
      {hasAddNotes && (
        <div
          className="px-3 py-2 text-[11px] text-[var(--color-text)] leading-relaxed line-clamp-2"
          style={{
            borderLeft: '4px solid var(--color-accent)',
            background: 'var(--color-accent-soft)',
          }}
          data-testid={`art-scene-block-${scene.scene_number}-additional-notes`}>
          <span className="text-[9px] font-bold uppercase tracking-wider mr-1" style={{ color: 'var(--color-accent)' }}>
            Additional Notes:
          </span>
          {scene.additional_notes}
        </div>
      )}
    </div>
  );
}

function SceneCard({ scene, buckets, onOpen, onPhotoClick }) {
  const allPhotos = Object.values(buckets).flat().flatMap(it => (it.image_paths || []).map(p => ({ path: p, item: it })));
  const hero = allPhotos[0];
  const totalPhotos = allPhotos.length;
  const counts = {
    action:   buckets.action_props.length,
    scene:    buckets.scene_props.length,
    dressing: buckets.set_dressing.length,
    vehicle:  buckets.picture_vehicle.length,
  };
  return (
    <div
      className="border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-panel)] hover:border-[var(--color-accent)] cursor-pointer transition"
      onClick={() => onOpen(scene)}
      data-testid={`art-scene-card-${scene.scene_number}`}
    >
      <div className="relative aspect-[16/9] bg-[var(--color-bg)]">
        {hero ? (
          <AuthImage path={hero.path} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)]">
            <MapPin className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1">
          <TypeBadge type={scene.location_type} />
          <TodBadge tod={scene.time_of_day} />
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/70 text-[var(--color-accent)]">#{formatSceneNumber(scene.scene_number)}</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
          <div className="text-sm font-bold text-white truncate">{scene.location_name || '—'}</div>
        </div>
      </div>
      <div className="p-2">
        {scene.synopsis && (
          <div className="text-[11px] text-[var(--color-text-sec)] line-clamp-2 mb-2">
            {scene.synopsis_en || scene.synopsis}
          </div>
        )}
        <div className="text-[10px] text-[var(--color-text-dim)] mb-2 flex gap-2">
          <span>Action·{counts.action}</span>
          <span>Scene·{counts.scene}</span>
          <span>Dressing·{counts.dressing}</span>
          <span>Vehicle·{counts.vehicle}</span>
        </div>
        {totalPhotos > 0 && (
          <div className="flex gap-1 mb-2">
            {allPhotos.slice(0,4).map((p, i) => (
              <button key={i} type="button" onClick={(e) => { e.stopPropagation(); onPhotoClick?.(p.item, (p.item.image_paths || []).indexOf(p.path)); }}
                      className="w-12 h-12 rounded overflow-hidden border border-[var(--color-border)]"
                      data-testid={`art-card-filmstrip-thumb-${i}`}>
                <AuthImage path={p.path} className="w-full h-full object-cover" alt="" />
              </button>
            ))}
            {totalPhotos > 4 && (
              <div className="w-12 h-12 rounded bg-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-center text-[10px] text-[var(--color-text-sec)]">
                +{totalPhotos - 4}
              </div>
            )}
          </div>
        )}
        <div className="text-[11px] text-[var(--color-accent)]">{L('common.view_details')} →</div>
      </div>
    </div>
  );
}

// ── Phase 9: Read-only rollup of Special Requirements linked to this scene ──
// Fetches on scene change; opens the RequirementModal via context for
// view/edit; provides "+ Link requirement" (existing) and
// "+ New requirement (this scene)" (create with linked_scene_ids pre-filled).
export function SceneRequirementsRollup({ sceneId, projectId, token, canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);
  const { openRequirementModal } = useRequirementActions();

  const reload = useCallback(async () => {
    if (!sceneId) return;
    setLoading(true);
    try {
      const r = await authAxios.get(
        `/projects/${projectId}/art/requirements?linked_scene_id=${sceneId}`,
      );
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, projectId, sceneId]);
  useEffect(() => { reload(); }, [reload]);

  const onCreated = useCallback((saved) => {
    setItems(prev => {
      const i = prev.findIndex(x => x.id === saved.id);
      if (i < 0) return [saved, ...prev];
      const nx = prev.slice(); nx[i] = saved; return nx;
    });
  }, []);

  const linkExisting = async () => {
    // Prompt picker over unlinked project requirements.
    try {
      const r = await authAxios.get(`/projects/${projectId}/art/requirements`);
      const all = r.data.items || [];
      const unlinked = all.filter(x => !(x.linked_scene_ids || []).includes(sceneId));
      if (unlinked.length === 0) { toast.info('No other requirements to link.'); return; }
      const label = unlinked.map((x, i) => `${i + 1}. [${(x.priority || 'std').toUpperCase()}] ${x.title}`).join('\n');
      const pick = window.prompt(`Link an existing requirement:\n\n${label}\n\nEnter number:`);
      const n = parseInt(pick || '', 10);
      if (!Number.isFinite(n) || n < 1 || n > unlinked.length) return;
      const chosen = unlinked[n - 1];
      const nextIds = [...(chosen.linked_scene_ids || []), sceneId];
      const patched = await authAxios.patch(
        `/projects/${projectId}/art/requirements/${chosen.id}`,
        { linked_scene_ids: nextIds },
      );
      onCreated(patched.data);
      toast.success('Linked to scene');
    } catch (e) { console.error(e); toast.error('Link failed'); }
  };

  const rowClick = (req) => {
    openRequirementModal({ mode: 'edit', req, onCreated });
  };

  const empty = !loading && items.length === 0;
  if (empty && !canWrite) return null;   // §9.8 — hide entirely in read mode if empty

  return (
    <section className="p-4 border-b border-[var(--color-border)]" data-testid="art-drawer-requirements-rollup">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)]">
          Special Requirements ({items.length})
        </div>
        <span className="text-[9px] text-[var(--color-accent)] hover:underline cursor-pointer ml-auto"
          onClick={() => {
            try { window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'special_requirements', linked_scene_id: sceneId } })); }
            catch { /* noop */ }
          }}
          data-testid="art-drawer-requirements-manage-link">
          Manage in tab →
        </span>
      </div>
      {loading && <div className="text-[11px] italic text-[var(--color-text-mute)]">Loading…</div>}
      {empty && (
        <div className="text-[11px] italic text-[var(--color-text-mute)] mb-2">
          No requirements linked to this scene yet.
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map(r => (
            <button key={r.id} type="button" onClick={() => rowClick(r)}
              className="w-full text-left flex items-center gap-2 py-1 px-2 rounded hover:bg-[var(--color-panel)]"
              data-testid={`art-drawer-req-row-${r.id}`}>
              <ReqPriorityPill priority={r.priority} />
              <ReqCategoryPill category={r.category} />
              <span className="text-[11px] text-[var(--color-text)] flex-1 truncate">{r.title}</span>
              <ReqStatusPill status={r.status} />
            </button>
          ))}
        </div>
      )}
      {canWrite && (
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={linkExisting}
            className="text-[10px] px-2 py-1 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
            data-testid="art-drawer-req-link-btn">
            + Link requirement
          </button>
          <button type="button"
            onClick={() => openRequirementModal({
              mode: 'create',
              prefill: { linked_scene_ids: [sceneId] },
              onCreated,
            })}
            className="text-[10px] px-2 py-1 rounded border border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20"
            data-testid="art-drawer-req-new-btn">
            + New requirement (this scene)
          </button>
        </div>
      )}
    </section>
  );
}

export function Drawer({ scene, buckets, palettes, moods, onClose, onPhotoClick,
                  canWrite, projectId, token,
                  onSceneMutate, onPaletteMutate, onMoodMutate, onItemMutate,
                  locations = [], setLocations = null }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const { openAssetModal } = useAssetActions();

  // Commit D · Bulk-add modal state (Drawer-scoped). `bulkAddCol` is the
  // colKey currently being bulk-added into ('action_props' |
  // 'scene_props' | 'set_dressing'), or null when the modal is closed.
  const [bulkAddCol, setBulkAddCol] = useState(null);
  // Phase 13 Gap 1 — full-field edit modal (superset of inline description edit).
  const [editingItem, setEditingItem] = useState(null);
  const openFullEdit = useCallback((item) => setEditingItem(item), []);

  // ── Notes (art_notes) — Save/Cancel model ──────────────────────────
  const [notesDraft, setNotesDraft] = useState(scene?.art_notes || '');
  const [notesDirty, setNotesDirty] = useState(false);
  useEffect(() => { setNotesDraft(scene?.art_notes || ''); setNotesDirty(false); }, [scene?.id, scene?.art_notes]);

  // ── Additional Notes (Phase 4.5, art-dept technical instructions) ──
  const [addNotesDraft, setAddNotesDraft] = useState(scene?.additional_notes || '');
  const [addNotesDirty, setAddNotesDirty] = useState(false);
  useEffect(() => {
    setAddNotesDraft(scene?.additional_notes || '');
    setAddNotesDirty(false);
  }, [scene?.id, scene?.additional_notes]);

  const anyNotesDirty = notesDirty || addNotesDirty;

  const saveNotes = async () => {
    try {
      const payload = {};
      if (notesDirty)    payload.art_notes        = notesDraft;
      if (addNotesDirty) payload.additional_notes = addNotesDraft;
      if (Object.keys(payload).length === 0) return;
      await authAxios.patch(`/scenes/${scene.id}`, payload);
      onSceneMutate?.({ ...scene, ...payload });
      setNotesDirty(false);
      setAddNotesDirty(false);
      toast.success('Notes saved');
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    }
  };

  const cancelNotes = () => {
    setNotesDraft(scene?.art_notes || '');
    setAddNotesDraft(scene?.additional_notes || '');
    setNotesDirty(false);
    setAddNotesDirty(false);
  };

  // ── Prop chip operations (per-chip, per-column) ────────────────────
  // Bucket key → legacy BreakdownCategory + asset_sub_type payload.
  const bucketToPayload = (colKey, description) => {
    // Phase 12a.1 Item 1-C — direct-add to Action Props sets the manual
    // flag rather than the legacy `asset_sub_type` shim.
    if (colKey === 'action_props') return { category: 'PROP', is_action_prop: true, description };
    if (colKey === 'scene_props')   return { category: 'PROP', description };
    if (colKey === 'set_dressing')  return { category: 'ART_SET_DRESSING', description };
    if (colKey === 'picture_vehicle') return { category: 'PICTURE_VEHICLE', description };
    return { category: 'PROP', description };
  };

  const handleAdd = async (colKey, description) => {
    try {
      const payload = { scene_id: scene.id, ...bucketToPayload(colKey, description) };
      const r = await authAxios.post(`/projects/${projectId}/breakdown-items`, payload);
      onItemMutate?.('add', r.data);
      toast.success('Added');
    } catch (e) {
      console.error(e);
      toast.error('Add failed');
    }
  };

  // Commit D · bulk-add loop — sequentially calls the SAME single-add
  // endpoint (`POST /projects/{pid}/breakdown-items`) once per parsed
  // entry. Intentionally throws on non-2xx so the caller (BulkAddModal)
  // can tally per-item failures without giving up mid-run.
  const handleBulkAddOne = async (colKey, description) => {
    const payload = { scene_id: scene.id, ...bucketToPayload(colKey, description) };
    const r = await authAxios.post(`/projects/${projectId}/breakdown-items`, payload);
    onItemMutate?.('add', r.data);
  };

  // Commit A · promote/demote — optimistic UI: flip the flag locally
  // (which re-buckets via bucketOf), then PATCH. On failure, revert.
  const handleToggleActionProp = async (item) => {
    const next = !item.is_action_prop;
    onItemMutate?.('update', { ...item, is_action_prop: next });
    try {
      await authAxios.patch(`/breakdown-items/${item.id}`, { is_action_prop: next });
      toast.success(next ? 'Promoted to Action Prop' : 'Demoted to Scene Prop');
    } catch (e) {
      console.error(e);
      onItemMutate?.('update', { ...item }); // revert
      toast.error('Update failed');
    }
  };

  const handleEditChip = async (item, description) => {
    try {
      await authAxios.patch(`/breakdown-items/${item.id}`, { description });
      onItemMutate?.('update', { ...item, description });
    } catch (e) {
      console.error(e);
      toast.error('Update failed');
    }
  };

  const handleDeleteChip = async (item) => {
    // Phase 6.5: inherited Base Kit chip → soft-exclude for this scene.
    // Two-option dialog: "Hide from this scene only" vs "Remove from
    // <location> entirely". A confirm() with an intermediate prompt is
    // sufficient for MVP; the location-entirely path is intentionally
    // gated behind an extra confirm to avoid catastrophic wide-scoped
    // removes.
    if (item.__inherited_from_base_kit) {
      const wideChoice = window.confirm(
        `"${item.description}" is inherited from this location's Base Kit.\n\n` +
        `OK   = Hide from THIS scene only\n` +
        `Cancel = Choose Base-Kit-wide action`
      );
      if (wideChoice) {
        // Hide from this scene: append asset id to excluded_base_kit_ids
        const cur = new Set(scene.excluded_base_kit_ids || []);
        cur.add(item.__asset_id);
        try {
          await authAxios.patch(`/scenes/${scene.id}`, { excluded_base_kit_ids: Array.from(cur) });
          onSceneMutate?.({ ...scene, excluded_base_kit_ids: Array.from(cur) });
          toast.success('Hidden from this scene');
        } catch (e) { console.error(e); toast.error('Update failed'); }
      } else {
        const wide = window.confirm(
          `Remove "${item.description}" from the ENTIRE location's Base Kit?\n\n` +
          `Every scene at this location will lose the chip.`
        );
        if (!wide) return;
        // Remove asset_id from location.base_kit_asset_ids
        const locKey = (scene.location_name || '').trim().toLowerCase();
        const loc = locations.find(l => (l.name || '').trim().toLowerCase() === locKey);
        if (!loc?.id) { toast.error('Location not found'); return; }
        const nextIds = (loc.base_kit_asset_ids || []).filter(x => x !== item.__asset_id);
        try {
          await authAxios.patch(
            `/projects/${projectId}/art/locations/${loc.id}`,
            { base_kit_asset_ids: nextIds },
          );
          // Mutate local locations state
          setLocations?.(prev => prev.map(l => l.id === loc.id ? { ...l, base_kit_asset_ids: nextIds } : l));
          toast.success(`Removed from ${loc.name}`);
        } catch (e) { console.error(e); toast.error('Update failed'); }
      }
      return;
    }
    if (!window.confirm(`Delete "${item.description}"?`)) return;
    try {
      await authAxios.delete(`/breakdown-items/${item.id}`);
      onItemMutate?.('delete', item);
      toast.success('Deleted');
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  const handleConvertToAsset = (item) => {
    // Phase 8: use AssetActionsContext instead of window.__afterAssetCreated
    if (!openAssetModal) {
      toast.error('Asset modal not available.');
      return;
    }
    const catMap = {
      PROP: (item.asset_sub_type === 'action_prop' || item.asset_sub_type === 'weapon' || item.asset_sub_type === 'hero_prop')
        ? 'action_prop' : 'scene_prop',
      ART_SET_DRESSING: 'set_dressing',
      PICTURE_VEHICLE: 'picture_vehicle',
    };
    const cat = catMap[(item.category || '').toUpperCase()] || 'scene_prop';
    openAssetModal({
      prefill: {
        name: item.description || '',
        category: cat,
        linked_scene_ids: [scene.id],
      },
      onCreated: async (saved) => {
        try {
          await authAxios.patch(`/breakdown-items/${item.id}`, { asset_id: saved.id });
          onItemMutate?.('update', { ...item, asset_id: saved.id });
          toast.success('Converted to asset');
        } catch (e) { console.error(e); toast.error('Link failed'); }
      },
    });
  };

  const attachInputRef = useRef(null);
  const attachTargetRef = useRef(null);
  const handleAttachPhoto = (item) => {
    attachTargetRef.current = item;
    attachInputRef.current?.click();
  };
  const onAttachChange = async (e) => {
    const file = e.target.files?.[0];
    const item = attachTargetRef.current;
    e.target.value = '';
    if (!file || !item) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(`/breakdown-items/${item.id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPath = r.data?.storage_path;
      if (newPath) {
        onItemMutate?.('update', { ...item, image_paths: [...(item.image_paths || []), newPath] });
      }
      toast.success('Photo attached');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    }
  };

  // ── Phase 10 — AI Suggest Reference (Scene Props + Set Dressing only) ──
  const [suggestState, setSuggestState] = useState({ open: false, column: null, anchorRect: null });
  const openSuggestPanel = (column, btnEl) => {
    const rect = btnEl?.getBoundingClientRect ? btnEl.getBoundingClientRect() : null;
    setSuggestState({ open: true, column, anchorRect: rect });
  };
  const closeSuggestPanel = () => setSuggestState({ open: false, column: null, anchorRect: null });
  const suggestExistingNames = useMemo(() => {
    if (!suggestState.open) return [];
    if (suggestState.column === 'scene_prop') return (buckets.scene_props || []).map(x => x.description).filter(Boolean);
    if (suggestState.column === 'set_dressing') return (buckets.set_dressing || []).map(x => x.description).filter(Boolean);
    return [];
  }, [suggestState.open, suggestState.column, buckets.scene_props, buckets.set_dressing]);
  const onSuggestChipAdded = (chip) => {
    onItemMutate?.('add', chip);
  };

  // ── Look & feel — palette / mood board writes ──────────────────────
  const [showPaletteAdd, setShowPaletteAdd] = useState(false);
  const [paletteDraft, setPaletteDraft] = useState({ name: '', colors: ['#e8c547', '#a78bfa', '#3b82f6'] });
  const savePalette = async () => {
    if (!paletteDraft.name.trim()) { toast.error('Palette needs a name'); return; }
    try {
      const r = await authAxios.post(`/projects/${projectId}/palettes`, {
        department: 'ART',
        name: paletteDraft.name.trim(),
        colors: paletteDraft.colors.map(hex => ({ hex })),
        source: 'manual',
        scope: 'scene',
        scope_id: scene.id,
      });
      onPaletteMutate?.('add', r.data);
      setShowPaletteAdd(false);
      setPaletteDraft({ name: '', colors: ['#e8c547', '#a78bfa', '#3b82f6'] });
      toast.success('Palette added');
    } catch (e) { console.error(e); toast.error('Palette save failed'); }
  };
  const aiSuggestPalette = async () => {
    try {
      toast.loading('Generating palette…', { id: 'palette-ai' });
      const r = await authAxios.post(`/projects/${projectId}/palettes/ai-suggest`, {
        department: 'ART',
        name: `Scene ${formatSceneNumber(scene.scene_number)} palette`,
        tone: scene.synopsis_en || scene.synopsis || '',
        scope: 'scene',
        scope_id: scene.id,
      });
      onPaletteMutate?.('add', r.data);
      toast.success('Palette suggested', { id: 'palette-ai' });
    } catch (e) { console.error(e); toast.error('AI palette failed', { id: 'palette-ai' }); }
  };
  const deletePalette = async (p) => {
    if (!window.confirm(`Delete palette "${p.name}"?`)) return;
    try {
      await authAxios.delete(`/projects/${projectId}/palettes/${p.id}`);
      onPaletteMutate?.('delete', p);
    } catch (e) { console.error(e); toast.error('Delete failed'); }
  };

  const moodInputRef = useRef(null);
  const uploadMood = () => moodInputRef.current?.click();
  const onMoodUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios.post(
        `/projects/${projectId}/mood-board/upload`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { department: 'ART', scope: 'scene', scope_id: scene.id },
        },
      );
      onMoodMutate?.('add', r.data);
      toast.success('Mood image added');
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };
  const aiGenerateMood = async () => {
    const prompt = scene.synopsis_en || scene.synopsis || scene.location_name || 'cinematic scene reference';
    try {
      toast.loading('Generating mood image…', { id: 'mood-ai' });
      const r = await authAxios.post(`/projects/${projectId}/mood-board/generate`, {
        department: 'ART',
        prompt,
        count: 1,
        scope: 'scene',
        scope_id: scene.id,
      });
      for (const img of (r.data?.images || [])) onMoodMutate?.('add', img);
      toast.success('Mood image generated', { id: 'mood-ai' });
    } catch (e) { console.error(e); toast.error('AI generation failed', { id: 'mood-ai' }); }
  };
  const deleteMood = async (m) => {
    if (!window.confirm('Delete this mood image?')) return;
    try {
      await authAxios.delete(`/projects/${projectId}/mood-board/${m.id}`);
      onMoodMutate?.('delete', m);
    } catch (e) { console.error(e); toast.error('Delete failed'); }
  };
  // Ask 4 — inline caption update on a mood thumb.
  const patchMoodCaption = async (m, caption) => {
    if ((m.caption || '') === (caption || '')) return;
    try {
      const r = await authAxios.patch(
        `/projects/${projectId}/mood-board/${m.id}`,
        { caption },
      );
      onMoodMutate?.('update', r.data);
    } catch (e) { console.error(e); toast.error('Caption save failed'); }
  };

  if (!scene) return null;
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="art-scene-drawer"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      {/* Phase 12b.2 — backdrop is now pointer-events-none so Playwright
          and human users can hover chips through it. Click-to-close
          moved to the outer overlay wrapper above (target===currentTarget). */}
      <div className="flex-1 bg-black/50 pointer-events-none" aria-hidden="true" />
      <aside className="w-[560px] max-w-full h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] overflow-y-auto flex flex-col"
             onClick={(e) => e.stopPropagation()}>
        {/* Commit B (Phase 12a.1 Item 7-i, 2026-02) — vertical stack:
            metadata row on line 1, location name below with a generous
            `--sp-*` gap so the location reads as its own scanline. */}
        <header
          className="sticky top-0 px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)] z-10 flex flex-col"
          style={{ rowGap: 'var(--sp-2)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-[var(--color-accent)]">Scene {formatSceneNumber(scene.scene_number)}</span>
            <TypeBadge type={scene.location_type} />
            <TodBadge tod={scene.time_of_day} />
            {!canWrite && (
              <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[var(--color-border-strong)] text-[var(--color-text-sec)]"
                    data-testid="art-drawer-readonly-badge">READ-ONLY</span>
            )}
            <button onClick={onClose} className="ml-auto text-[var(--color-text-dim)] hover:text-white" data-testid="art-drawer-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {scene.location_name && (
            <div
              className="flex items-center gap-1.5 min-w-0"
              style={{ marginTop: 'var(--sp-2)' }}
            >
              <MapPin className="w-3.5 h-3.5 text-[var(--color-text-dim)] shrink-0" />
              <span
                className="text-sm font-bold text-[var(--color-text)] truncate"
                data-testid="art-drawer-location-name"
              >
                {scene.location_name}
              </span>
            </div>
          )}
        </header>

        <div className="flex-1">
          <section className="p-4 border-b border-[var(--color-border)]">
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2">{L('drawer.synopsis')}</div>
            <div className="text-[12px] text-[var(--color-text)] leading-relaxed whitespace-pre-wrap">
              {scene.synopsis_en || scene.synopsis || <span className="italic text-[var(--color-text-mute)]">—</span>}
            </div>
          </section>

          <section className="p-4 border-b border-[var(--color-border)]">
            {/* Phase 4.5: tinted panels + 1px vertical dividers */}
            <div
              className="grid grid-cols-3"
              style={{ columnGap: '1px', background: PANEL_DIVIDER }}
            >
              <PropCol title={ART_COL_TOKENS.action_props.label} items={buckets.action_props} onPhotoClick={onPhotoClick} drawerMode
                canWrite={canWrite} colKey="action_props"
                onAdd={handleAdd} onEdit={handleEditChip} onDelete={handleDeleteChip} onAttachPhoto={handleAttachPhoto}
                onConvertToAsset={handleConvertToAsset}
                onToggleActionProp={handleToggleActionProp}
                onBulkAdd={setBulkAddCol}
                onEditFull={openFullEdit}
                onSuggest={(btnEl) => openSuggestPanel('action_prop', btnEl)} />
              <PropCol title={ART_COL_TOKENS.scene_props.label}  items={buckets.scene_props}  onPhotoClick={onPhotoClick} drawerMode
                canWrite={canWrite} colKey="scene_props"
                onAdd={handleAdd} onEdit={handleEditChip} onDelete={handleDeleteChip} onAttachPhoto={handleAttachPhoto}
                onConvertToAsset={handleConvertToAsset}
                onToggleActionProp={handleToggleActionProp}
                onBulkAdd={setBulkAddCol}
                onEditFull={openFullEdit}
                onSuggest={(btnEl) => openSuggestPanel('scene_prop', btnEl)} />
              <PropCol title={ART_COL_TOKENS.set_dressing.label} items={buckets.set_dressing} onPhotoClick={onPhotoClick} drawerMode
                canWrite={canWrite} colKey="set_dressing"
                onAdd={handleAdd} onEdit={handleEditChip} onDelete={handleDeleteChip} onAttachPhoto={handleAttachPhoto}
                onConvertToAsset={handleConvertToAsset}
                onBulkAdd={setBulkAddCol}
                onEditFull={openFullEdit}
                onSuggest={(btnEl) => openSuggestPanel('set_dressing', btnEl)} />
            </div>
          </section>

          <section className="p-4 border-b border-[var(--color-border)]">
            <PropCol title={ART_COL_TOKENS.picture_vehicle.label} items={buckets.picture_vehicle} onPhotoClick={onPhotoClick} drawerMode
              canWrite={canWrite} colKey="picture_vehicle"
              onAdd={handleAdd} onEdit={handleEditChip} onDelete={handleDeleteChip} onAttachPhoto={handleAttachPhoto}
              onConvertToAsset={handleConvertToAsset}
              onEditFull={openFullEdit} />
          </section>

          {canWrite && (
            <section className="px-4 py-2 border-b border-[var(--color-border)] flex gap-1 flex-wrap items-center">
              <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-dim)] mr-1">
                Promote to asset:
              </span>
              {[
                { k: 'action_prop',    label: '+ Action' },
                { k: 'scene_prop',     label: '+ Scene' },
                { k: 'set_dressing',   label: '+ Set dressing' },
                { k: 'picture_vehicle',label: '+ Vehicle' },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => openAssetModal({
                    prefill: { category: k, linked_scene_ids: [scene.id] },
                  })}
                  className="text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                  data-testid={`art-drawer-add-asset-${k}`}>
                  {label}
                </button>
              ))}
            </section>
          )}

          <input ref={attachInputRef} type="file" accept="image/*" onChange={onAttachChange}
                 className="hidden" data-testid="art-drawer-attach-input" />

          <section className="p-4 border-b border-[var(--color-border)]">
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2 flex items-center gap-2">
              <span>{L('drawer.look_and_feel')}</span>
              {canWrite && (
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={() => setShowPaletteAdd(v => !v)}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-accent)] hover:bg-[var(--color-border)]"
                    data-testid="art-palette-add-btn">
                    <Plus className="w-3 h-3 inline mr-1" /> Palette
                  </button>
                  <button type="button" onClick={aiSuggestPalette}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-accent)] hover:bg-[var(--color-border)]"
                    data-testid="art-palette-ai-btn">
                    <Sparkles className="w-3 h-3 inline mr-1" /> AI
                  </button>
                  <button type="button" onClick={uploadMood}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-accent)] hover:bg-[var(--color-border)]"
                    data-testid="art-mood-upload-btn">
                    <Upload className="w-3 h-3 inline mr-1" /> Mood
                  </button>
                  <button type="button" onClick={aiGenerateMood}
                    className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-accent)] hover:bg-[var(--color-border)]"
                    data-testid="art-mood-ai-btn">
                    <Sparkles className="w-3 h-3 inline mr-1" /> AI mood
                  </button>
                </div>
              )}
            </div>
            <input ref={moodInputRef} type="file" accept="image/*" onChange={onMoodUpload}
                   className="hidden" data-testid="art-drawer-mood-input" />

            {showPaletteAdd && (
              <div className="mb-3 p-3 bg-[var(--color-bg)] rounded border border-[var(--color-border)]" data-testid="art-palette-add-panel">
                <input value={paletteDraft.name}
                       onChange={(e) => setPaletteDraft(d => ({ ...d, name: e.target.value }))}
                       placeholder="Palette name"
                       className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-xs text-[var(--color-text)] mb-2"
                       data-testid="art-palette-name-input" />
                <div className="flex gap-2 mb-2">
                  {paletteDraft.colors.map((c, i) => (
                    <input key={i} type="color" value={c}
                      onChange={(e) => setPaletteDraft(d => {
                        const nx = [...d.colors]; nx[i] = e.target.value; return { ...d, colors: nx };
                      })}
                      className="w-8 h-8 rounded border border-[var(--color-border)] bg-transparent cursor-pointer"
                      data-testid={`art-palette-color-${i}`} />
                  ))}
                  <button type="button"
                    onClick={() => setPaletteDraft(d => ({ ...d, colors: [...d.colors, '#888888'] }))}
                    className="w-8 h-8 rounded border border-dashed border-[var(--color-border-strong)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
                    data-testid="art-palette-color-add">+</button>
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowPaletteAdd(false)}
                    className="text-xs px-3 py-1 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]"
                    data-testid="art-palette-cancel">Cancel</button>
                  <button type="button" onClick={savePalette}
                    className="text-xs px-3 py-1 rounded bg-[var(--color-accent)] text-black font-bold"
                    data-testid="art-palette-save">Save Palette</button>
                </div>
              </div>
            )}

            {palettes.length === 0 && moods.length === 0 ? (
              <div className="text-[11px] italic text-[var(--color-text-mute)]">{L('drawer.look_empty')}</div>
            ) : (
              <>
                {palettes.map(p => (
                  <div key={p.id} className="mb-2 group flex items-center gap-2" data-testid={`art-palette-${p.id}`}>
                    <div className="flex-1">
                      <div className="text-[10px] text-[var(--color-text-sec)] mb-1">{p.name}</div>
                      <div className="flex gap-1 flex-wrap">
                        {(p.colors || []).map((c, i) => (
                          <div key={i} className="w-6 h-6 rounded border border-[var(--color-border)]"
                               style={{ background: c.hex }} title={c.label || c.hex} />
                        ))}
                      </div>
                    </div>
                    {canWrite && (
                      <button type="button" onClick={() => deletePalette(p)}
                        className="opacity-0 group-hover:opacity-100 text-[var(--color-text-dim)] hover:text-[var(--color-priority-critical)]"
                        title="Delete palette"
                        data-testid={`art-palette-delete-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                {moods.length > 0 && (
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {moods.map(m => (
                      <div key={m.id} className="relative group aspect-square">
                        <button type="button"
                          className="w-full h-full rounded overflow-hidden border border-[var(--color-border)]"
                          onClick={() => onPhotoClick?.({ id: m.id, description: m.caption || 'Mood', image_paths: [m.storage_path] }, 0)}
                          data-testid="art-drawer-mood-thumb">
                          <AuthImage path={m.storage_path} className="w-full h-full object-cover" alt="" />
                        </button>
                        {canWrite && (
                          <button type="button" onClick={() => deleteMood(m)}
                            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5"
                            title="Delete mood"
                            data-testid={`art-mood-delete-${m.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="p-4 border-b border-[var(--color-border)]">
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2 flex items-center gap-2">
              <span>Art Notes</span>
              {notesDirty && <span className="text-[9px] text-[var(--color-accent)]">• unsaved</span>}
            </div>
            <Textarea
              value={notesDraft}
              disabled={!canWrite}
              onChange={(e) => { setNotesDraft(e.target.value); setNotesDirty(true); }}
              placeholder={canWrite ? 'Free-form art-department notes for this scene…' : 'No notes.'}
              className="min-h-[80px] bg-[var(--color-bg)] border border-[var(--color-border)] text-[12px] text-[var(--color-text)]"
              data-testid="art-drawer-notes-input"
            />
            {/* Ask 3 — image attach for scene art_notes (mood_board scope reuse) */}
            <NotesPhotoStrip
              projectId={projectId} token={token}
              scope="scene_art_notes" scopeId={scene.id}
              canWrite={canWrite}
              testKey="art-notes-photos" />
          </section>

          {/* Phase 4.5 — Additional Notes: art-dept technical instructions.
              Only rendered when there's content OR the caller has write
              access (empty + read-only viewers see nothing). */}
          {(canWrite || (scene.additional_notes && scene.additional_notes.trim())) && (
            <section
              className="p-4 border-b border-[var(--color-border)]"
              style={{
                borderLeft: '4px solid var(--color-accent)',
                background: 'var(--color-accent-soft)',
              }}
              data-testid="art-drawer-additional-notes-section"
            >
              <div className="text-[10px] font-bold uppercase mb-2 flex items-center gap-2"
                   style={{ color: 'var(--color-accent)' }}>
                <span>Additional Notes</span>
                {addNotesDirty && <span className="text-[9px] text-[var(--color-accent)]">• unsaved</span>}
              </div>
              <Textarea
                value={addNotesDraft}
                disabled={!canWrite}
                onChange={(e) => { setAddNotesDraft(e.target.value); setAddNotesDirty(true); }}
                placeholder={canWrite ? 'Add additional notes… (technical instructions, safety flags, art-dept-specific)' : ''}
                className="min-h-[70px] bg-[var(--color-bg)] border border-[var(--color-border)] text-[12px] text-[var(--color-text)]"
                data-testid="art-drawer-additional-notes-input"
              />
            </section>
          )}

          <SceneRequirementsRollup
            sceneId={scene.id}
            projectId={projectId}
            token={token}
            canWrite={canWrite}
          />
        </div>

        {canWrite && (
          <footer className="sticky bottom-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-t border-[var(--color-border)] z-10">
            <span className="text-[10px] text-[var(--color-text-dim)]">
              Chip / palette / mood edits save immediately. Notes require Save.
            </span>
            <div className="ml-auto flex gap-2">
              <button type="button"
                onClick={cancelNotes}
                disabled={!anyNotesDirty}
                className="text-xs px-3 py-1 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="art-drawer-cancel">
                Cancel notes
              </button>
              <button type="button"
                onClick={saveNotes}
                disabled={!anyNotesDirty}
                className="text-xs px-3 py-1 rounded bg-[var(--color-accent)] text-black font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="art-drawer-save">
                <Check className="w-3 h-3 inline mr-1" /> Save notes
              </button>
            </div>
          </footer>
        )}
      </aside>
      {/* Phase 10 — AI Suggest Reference floating panel */}
      <AiSuggestPanel
        open={suggestState.open}
        anchorRect={suggestState.anchorRect}
        projectId={projectId}
        token={token}
        sceneId={scene?.id}
        column={suggestState.column}
        existingNames={suggestExistingNames}
        onClose={closeSuggestPanel}
        onChipAdded={onSuggestChipAdded}
      />
      {/* Commit D — Bulk-add modal (Drawer-scoped) */}
      <BulkAddModal
        open={!!bulkAddCol}
        colKey={bulkAddCol}
        title={bulkAddCol ? `${ART_COL_TOKENS[bulkAddCol].label} · Scene ${formatSceneNumber(scene.scene_number)}` : ''}
        existingNames={bulkAddCol ? (buckets[bulkAddCol] || []).map(i => i.description).filter(Boolean) : []}
        onClose={() => setBulkAddCol(null)}
        onSubmit={handleBulkAddOne}
      />
      {/* Phase 13 Gap 1 — full-field edit modal */}
      <BreakdownItemEditModal
        open={!!editingItem}
        item={editingItem}
        projectId={scene?.project_id}
        token={token}
        onClose={() => setEditingItem(null)}
        onSaved={(merged) => {
          onItemMutate?.('update', merged);
          setEditingItem(null);
        }}
      />
    </div>
  );
}

// Lightbox moved to ./artShared.jsx (Phase 5, 2026-02).

export default function ArtByScene({ projectId, token }) {
  const [scenes, setScenes] = useState([]);
  const [items, setItems] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [moods, setMoods] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('art.by_scene.view') || 'table');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');  // all | INT | EXT
  const [filterTod,  setFilterTod]  = useState('all');  // all | DAY | NIGHT
  const [selectedScene, setSelectedScene] = useState(null);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const { permissions } = useProjectRole(projectId);
  const { user } = useAuth();
  // Art module (2026-07) — write affordances open to any authenticated user
  // with project access. Backend routes still enforce their own auth
  // (superadmin bypass, project membership, etc.).
  const canWrite = !!user || !!permissions?.canWrite;

  // Phase 13.5 (2026-07) — Cause B fix: parent-owned modal for compact-list
  // chip pencils. Drawer keeps its own modal instance (line ~1023).
  const [compactEditingItem, setCompactEditingItem] = useState(null);
  const openCompactEdit = useCallback((item) => setCompactEditingItem(item), []);

  useEffect(() => { localStorage.setItem('art.by_scene.view', view); }, [view]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        // `/breakdown` returns a list of scenes; each scene has
        // `breakdown_items[]` embedded. Single request, no separate `/scenes`
        // endpoint (that path returns 404 today).
        const [br, pal, mb, locs, assets] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/breakdown`,{ headers }),
          axios.get(`${API}/projects/${projectId}/palettes?department=ART`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/projects/${projectId}/mood-board?department=ART`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/projects/${projectId}/art/locations`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/projects/${projectId}/art/assets`, { headers }).catch(() => ({ data: { items: [] } })),
        ]);
        const sceneList = Array.isArray(br.data) ? br.data : (br.data?.scenes || []);
        setScenes(sceneList);
        // Phase 13 Ask D (2026-07) — deep-link from Master Breakdown:
        // /projects/{pid}/dept/art?scene=<sid> auto-opens that scene's drawer.
        // Read once at load time (before any other state settles) to avoid
        // racing with user interaction. Fires only when the query param is
        // present — no default auto-open.
        const params = new URLSearchParams(window.location.search);
        const focusSceneId = params.get('scene');
        if (focusSceneId) {
          const target = sceneList.find(s => s.id === focusSceneId);
          if (target) setSelectedScene(target);
        }
        const flat = [];
        for (const s of sceneList) {
          // `/breakdown` returns `breakdown_items` as a DICT keyed by
          // legacy category (e.g. `PROP`, `ART_SET_DRESSING`, `COSTUME`,
          // `PICTURE_VEHICLE`, `PRODUCTION_NOTE`, ...) mapping to a list
          // of items — not a flat list. Flatten via Object.values so
          // every persisted chip lands in `items` and shows up in the
          // drawer on reload. Guards: some legacy scenes may still ship
          // a plain array; the Array.isArray branch keeps them working.
          const bd = s.breakdown_items;
          if (Array.isArray(bd)) {
            for (const it of bd) flat.push({ ...it, scene_id: it.scene_id || s.id });
          } else if (bd && typeof bd === 'object') {
            for (const catArr of Object.values(bd)) {
              for (const it of (catArr || [])) flat.push({ ...it, scene_id: it.scene_id || s.id });
            }
          }
        }
        setItems(flat);
        setPalettes((pal.data || []).filter(p => p.scope === 'scene'));
        setMoods((mb.data?.images || mb.data || []).filter(m => m.scope === 'scene'));
        setLocations(locs.data || []);
        setAssets(assets.data?.items || []);
      } catch (e) {
        console.error('ArtByScene load failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, token]);

  const itemsByScene = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.scene_id) continue;
      (map[it.scene_id] ||= []).push(it);
    }
    return map;
  }, [items]);

  const bucketsForScene = useCallback((sceneId) => {
    const list = itemsByScene[sceneId] || [];
    const b = { action_props: [], scene_props: [], set_dressing: [], picture_vehicle: [] };
    for (const it of list) {
      const k = bucketOf(it);
      if (k) b[k].push(it);
    }
    // Phase 6.5: read-time join — prepend inherited Base Kit chips
    // computed as (location.base_kit_asset_ids − scene.excluded_base_kit_ids).
    // Inherited chips carry the marker `__inherited_from_base_kit: true`
    // which the shared Chip renderer picks up to show the 📍 badge and
    // switch the delete affordance to "exclude from this scene".
    const scene = scenes.find(s => s.id === sceneId);
    if (!scene) return b;
    const locKey = (scene.location_name || '').trim().toLowerCase();
    const loc = locations.find(l => (l.name || '').trim().toLowerCase() === locKey);
    if (!loc || !(loc.base_kit_asset_ids?.length)) return b;
    const excluded = new Set(scene.excluded_base_kit_ids || []);
    const inheritedIds = loc.base_kit_asset_ids.filter(id => !excluded.has(id));
    const inherited = [];
    for (const aid of inheritedIds) {
      const a = assets.find(x => x.id === aid);
      if (!a) continue;
      inherited.push({
        id: `inherited-${aid}`,
        __inherited_from_base_kit: true,
        __asset_id: aid,
        description: a.name,
        image_paths: (a.photos || []).map(p => p.storage_path),
        category: (
          a.category === 'action_prop' ? 'PROP'
          : a.category === 'set_dressing' ? 'ART_SET_DRESSING'
          : a.category === 'picture_vehicle' ? 'PICTURE_VEHICLE'
          : 'PROP'
        ),
        asset_sub_type: a.category === 'action_prop' ? 'action_prop' : undefined,
      });
    }
    for (const it of inherited) {
      const k = bucketOf(it);
      if (k) b[k].unshift(it);   // prepend inherited to top of column
    }
    return b;
  }, [itemsByScene, scenes, locations, assets]);

  const filteredScenes = useMemo(() => {
    let list = [...(scenes || [])].sort((a,b) => (a.scene_number||0) - (b.scene_number||0));
    if (filterType !== 'all') list = list.filter(s => (s.location_type || 'INT').toUpperCase().startsWith(filterType));
    if (filterTod  !== 'all') list = list.filter(s => (s.time_of_day  || 'DAY').toUpperCase() === filterTod);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(s => String(s.scene_number).includes(q) ||
                                    (s.synopsis || '').toLowerCase().includes(q) ||
                                    (s.synopsis_en || '').toLowerCase().includes(q) ||
                                    (s.location_name || '').toLowerCase().includes(q));
    return list;
  }, [scenes, filterType, filterTod, search]);

  const scenePalettes = useMemo(() => selectedScene ? palettes.filter(p => p.scope_id === selectedScene.id) : [], [selectedScene, palettes]);
  const sceneMoods    = useMemo(() => selectedScene ? moods.filter(m => m.scope_id === selectedScene.id)    : [], [selectedScene, moods]);

  // ── Drawer mutation callbacks — keep local state in sync ────────────
  const handleSceneMutate = useCallback((updated) => {
    setScenes(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
    setSelectedScene(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
  }, []);
  const handleItemMutate = useCallback((op, item) => {
    if (op === 'add')    setItems(prev => [...prev, item]);
    if (op === 'update') setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...item } : i));
    if (op === 'delete') setItems(prev => prev.filter(i => i.id !== item.id));
  }, []);
  const handlePaletteMutate = useCallback((op, p) => {
    if (op === 'add')    setPalettes(prev => [...prev, p]);
    if (op === 'delete') setPalettes(prev => prev.filter(x => x.id !== p.id));
  }, []);
  const handleMoodMutate = useCallback((op, m) => {
    if (op === 'add')    setMoods(prev => [...prev, m]);
    if (op === 'delete') setMoods(prev => prev.filter(x => x.id !== m.id));
    if (op === 'update') setMoods(prev => prev.map(x => x.id === m.id ? { ...x, ...m } : x));
  }, []);

  const openPhotoLightbox = useCallback((item, photoIndex) => {
    // Build lightbox list for the CURRENT scene, walk all photos across all props ordered by category → item order.
    const sceneId = item.scene_id || (selectedScene && selectedScene.id);
    if (!sceneId) return;
    const scene = scenes.find(s => s.id === sceneId);
    const b = bucketsForScene(sceneId);
    const order = ['action_props','scene_props','set_dressing','picture_vehicle'];
    const walk = [];
    for (const k of order) {
      for (const it of b[k]) {
        for (const p of (it.image_paths || [])) {
          walk.push({ path: p, itemName: it.description || '—', sceneNumber: scene?.scene_number });
        }
      }
    }
    // Also include mood-board photos linked to this scene (Phase 4+ they'll be scope=scene)
    for (const m of sceneMoods) walk.push({ path: m.storage_path, itemName: m.caption || 'Mood', sceneNumber: scene?.scene_number });
    if (walk.length === 0) return;
    // Find the clicked photo in the walk
    const target = (item.image_paths || [])[photoIndex];
    const idx = target ? walk.findIndex(w => w.path === target) : 0;
    setLightboxItems(walk);
    setLightboxIndex(idx >= 0 ? idx : 0);
  }, [scenes, sceneMoods, selectedScene, bucketsForScene]);

  return (
    <div className="p-4" data-testid="art-by-scene-view">
      {/* Persistent header controls */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Search className="w-3 h-3 text-[var(--color-text-dim)]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={L('search.placeholder')}
            className="bg-transparent border-0 h-6 text-xs w-48"
            data-testid="art-search-input"
          />
        </div>
        <div className="flex items-center gap-1 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Filter className="w-3 h-3 text-[var(--color-text-dim)]" />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-transparent text-xs text-[var(--color-text)]" data-testid="art-filter-type">
            <option value="all">All types</option><option value="INT">INT</option><option value="EXT">EXT</option>
          </select>
          <select value={filterTod} onChange={(e) => setFilterTod(e.target.value)} className="bg-transparent text-xs text-[var(--color-text)]" data-testid="art-filter-tod">
            <option value="all">All times</option><option value="DAY">DAY</option><option value="NIGHT">NIGHT</option>
          </select>
        </div>
        <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
          <button onClick={() => setView('table')}
            className={`px-3 py-1 text-xs ${view === 'table' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-view-table">Table</button>
          <button onClick={() => setView('cards')}
            className={`px-3 py-1 text-xs ${view === 'cards' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-view-cards">Cards</button>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          {/* Phase 12a — scene coverage badge (informational, muted).
              Derived read-time: scenes with ≥1 prop having photo_ids. */}
          {(() => {
            const total = scenes.length;
            const covered = scenes.filter(s => {
              const b = bucketsForScene(s.id);
              const all = [...(b.action_props||[]), ...(b.scene_props||[]), ...(b.set_dressing||[]), ...(b.picture_vehicle||[])];
              return all.some(it => (it.image_paths || []).length > 0);
            }).length;
            if (!total) return null;
            return (
              <span className="text-[10px] px-2 py-1 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-dim)]"
                    title="Scenes with at least one prop having reference photos"
                    data-testid="art-coverage-badge">
                📷 {covered}/{total} scenes covered
              </span>
            );
          })()}
          <Button title="Coming in Phase 11" disabled className="bg-[var(--color-panel)] text-[var(--color-text-dim)] text-xs h-7 px-3 opacity-60 cursor-not-allowed" data-testid="art-export-btn">
            <Download className="w-3 h-3 mr-1" /> Export PDF
          </Button>
          <Button title={L('add_asset.disabled_tooltip')} disabled className="bg-[var(--color-panel)] text-[var(--color-text-dim)] text-xs h-7 px-3 opacity-60 cursor-not-allowed" data-testid="art-add-asset-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Asset
          </Button>
        </div>
      </div>

      {loading && <div className="text-center text-[var(--color-text-dim)] text-sm py-8">Loading…</div>}
      {!loading && filteredScenes.length === 0 && (
        <div className="text-center text-[var(--color-text-dim)] text-sm py-8" data-testid="art-empty">
          No scenes match the current filters.
        </div>
      )}
      {!loading && view === 'table' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="art-table-view">
          {filteredScenes.map(s => (
            <SceneBlock key={s.id}
              scene={s}
              buckets={bucketsForScene(s.id)}
              onOpen={setSelectedScene}
              onPhotoClick={openPhotoLightbox}
              canWrite={canWrite}
              onEditFull={openCompactEdit}
            />
          ))}
        </div>
      )}
      {!loading && view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="art-cards-view">
          {filteredScenes.map(s => (
            <SceneCard key={s.id}
              scene={s}
              buckets={bucketsForScene(s.id)}
              onOpen={setSelectedScene}
              onPhotoClick={openPhotoLightbox}
            />
          ))}
        </div>
      )}

      {selectedScene && (
        <Drawer
          scene={selectedScene}
          buckets={bucketsForScene(selectedScene.id)}
          palettes={scenePalettes}
          moods={sceneMoods}
          onClose={() => setSelectedScene(null)}
          onPhotoClick={openPhotoLightbox}
          canWrite={canWrite}
          projectId={projectId}
          token={token}
          onSceneMutate={handleSceneMutate}
          onItemMutate={handleItemMutate}
          onPaletteMutate={handlePaletteMutate}
          onMoodMutate={handleMoodMutate}
          locations={locations}
          setLocations={setLocations}
        />
      )}

      <Lightbox
        items={lightboxItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onNav={(delta) => setLightboxIndex(i => {
          if (!lightboxItems.length) return -1;
          const n = (i + delta + lightboxItems.length) % lightboxItems.length;
          return n;
        })}
      />

      {/* Phase 13.5 (2026-07) — Cause B: parent-level modal for compact-list
          chip pencils. Drawer keeps its own modal instance intact. */}
      <BreakdownItemEditModal
        open={!!compactEditingItem}
        item={compactEditingItem}
        projectId={projectId}
        token={token}
        onClose={() => setCompactEditingItem(null)}
        onSaved={(merged) => {
          if (merged) handleItemMutate('update', merged);
          setCompactEditingItem(null);
        }}
      />
    </div>
  );
}
